import { ImageUploadModal } from "@/components/avatar-upload/ImageUploadModal.tsx";
import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApplicantLabels } from "@/pages/recruit/candidates/components/ApplicantLabels.tsx";
import { ApplicantProfileComments } from "@/pages/recruit/candidates/components/ApplicantProfileComments.tsx";
import { HiredTag } from "@/pages/recruit/candidates/components/HiredTag.tsx";
import { PotentialThreatTag } from "@/pages/recruit/candidates/components/PotentialThreatTag.tsx";
import { ApplicantProfileFields } from "@/pages/recruit/candidates/components/ApplicantProfileFields.tsx";
import ApplicantProfileCV from "@/pages/recruit/candidates/components/ApplicantProfileCV.tsx";
import { ApplicantEditModal } from "@/pages/recruit/candidates/widgets/ApplicantEditModal.tsx";
import { ApplicantIgnoredModal } from "@/pages/recruit/candidates/widgets/ApplicantIgnoredModal.tsx";
import { ApplicantMergeWithModal } from "@/pages/recruit/candidates/widgets/ApplicantMergeWithModal.tsx";
import { ApplicantsMergeModal } from "@/pages/recruit/candidates/widgets/ApplicantsMergeModal.tsx";
import {
  useApplicantDelete,
  useApplicantDeletePhoto,
  useApplicantSetPhoto,
} from "@/queries/recruit/use-applicants.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useAppTheme } from "@/themes/app-context.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { ApiApplicant, ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Avatar,
  Box,
  Button,
  ButtonIcon,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export interface ApplicantProfileProps {
  applicant: ApiApplicantShort | ApiApplicant | undefined;
  applicantFull: ApiApplicant | undefined;
}

export const ApplicantProfile = ({ applicant, applicantFull }: ApplicantProfileProps) => {
  const { t } = useTranslation();
  const modalsService = useModals();
  const { canIEdit } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(0);

  const applicantSetPhoto = useApplicantSetPhoto();
  const applicantDeletePhoto = useApplicantDeletePhoto();
  const applicantDelete = useApplicantDelete();
  const { hasField, isPending: isConfigPending } = useEntityFormConfig(ApiCFEntityTypes.applicant);

  const openNotes = location.pathname.includes("/notes");

  const handleEditApplicant = useCallback(() => {
    if (!applicantFull) return;

    modalsService.openModal(ApplicantEditModal, {
      applicant: applicantFull,
    });
  }, [applicantFull]);

  const body = useAppTheme("body");
  const canManageCandidate = canIEdit(ApiPermission.manage_candidates);
  const hasSimilarApplicants =
    !!applicantFull && applicantFull.similar_applicants.length > 0 && canManageCandidate;

  const handleMerge = useCallback(async () => {
    if (!applicantFull || applicantFull.similar_applicants.length == 0) return;

    modalsService.openModal(ApplicantsMergeModal, {
      applicant: applicantFull,
    });
  }, [applicant, applicantFull]);

  const handleMergeWith = () => {
    if (!applicantFull) return;

    modalsService.openModal(ApplicantMergeWithModal, {
      applicant: applicantFull,
    });
  };

  const handleOpenIgnored = () => {
    if (!applicantFull) return;

    modalsService.openModal(ApplicantIgnoredModal, {
      applicant: applicantFull,
    });
  };

  const closeProfile = () => {
    navigate(location.pathname.replace(/\/\d+(?:\/.*)?(?=\/|$|\?)/, "") + location.search);
  };

  const handleDelete = useCallback(async () => {
    if (!applicant) return;

    modalsService.openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("ats:delete_candidate_confirm_description"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await applicantDelete.mutateAsync(applicant.id);
          analyticsService.trackEvent(analyticEvents.applicants.deleted, {
            [analyticProperties.id]: applicant.id,
          });
          closeProfile();
        } catch {
          return;
        }
      },
    });
  }, [applicant]);

  const handleAvatarUpload = () => {
    if (!applicant) return;
    modalsService.openModal(ImageUploadModal, {
      modalTitle: t("common:photo"),
      path: ApiFilePathTypes.applicant_photo,
      onSave: async (file: ApiFile) => {
        try {
          await applicantSetPhoto.mutateAsync({
            id: applicant.id,
            fileUUID: file.uuid,
          });
          analyticsService.trackEvent(analyticEvents.applicants.edited, {
            [analyticProperties.id]: applicant.id,
            [analyticProperties.actionType]: "Photo Deleted",
          });
          return true;
        } catch {
          return false;
        }
      },
    });
  };

  const handleAvatarDelete = async () => {
    if (!applicant) return;

    try {
      await applicantDeletePhoto.mutateAsync(applicant.id);
      analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
        [analyticProperties.id]: applicant.id,
        [analyticProperties.actionType]: "Photo Deleted",
      });
    } catch {
      return;
    }
  };

  const cvs = applicantFull?.cvs;
  const cvsCount = cvs?.length;
  const applicantId = applicant?.id;
  const [syncedOpenNotes, setSyncedOpenNotes] = useState<boolean>();
  const [syncedCvsCount, setSyncedCvsCount] = useState(cvsCount);
  const [syncedApplicantId, setSyncedApplicantId] = useState(applicantId);
  if (
    openNotes !== syncedOpenNotes ||
    cvsCount !== syncedCvsCount ||
    applicantId !== syncedApplicantId
  ) {
    setSyncedOpenNotes(openNotes);
    setSyncedCvsCount(cvsCount);
    setSyncedApplicantId(applicantId);
    if (cvs) {
      if (openNotes) {
        setActiveTab(cvs.length);
      } else {
        setActiveTab(0);
      }
    }
  }

  const handleActiveTabChange = useCallback(
    (activeTabIndex: number) => {
      if (!applicant || !cvs) return;

      setActiveTab(activeTabIndex);

      const isNotesTab = activeTabIndex >= cvs.length;

      if (isNotesTab && !openNotes) {
        navigate(`/recruitment/candidates/${applicant.id}/notes${location.search}`);
      } else if (!isNotesTab && openNotes) {
        navigate(`/recruitment/candidates/${applicant.id}${location.search}`);
      }
    },
    [cvs, openNotes, applicant, location.search, navigate]
  );

  const getNotesTabRightIcon = useCallback(() => {
    return applicant?.have_unread_mentions &&
      applicant?.last_or_unread_comment_data?.entity_type === ApiEntityTypes.applicant ? (
      <Box className="w-2 h-2 bg-new rounded-full" />
    ) : undefined;
  }, [applicant]);

  return (
    <>
      {applicant && (
        <Stack>
          <div className="relative h-40 shrink-0">
            {applicantFull && (
              <>
                {applicantFull.color_palette && applicantFull.color_palette.length >= 2 && (
                  <GradientContainer
                    colorPalette={applicantFull.color_palette}
                    addLightOverlayTop={true}
                  />
                )}
              </>
            )}
          </div>
          <Box className={twMerge("w-full h-40 transition-all relative -mt-40", body.base)}>
            <Box className={"absolute inset-0"}>
              <Stack
                direction={"horizontal"}
                justify={"between"}
                items={"center"}
                gap={"sm"}
                className={"px-6 py-4 w-full h-16"}
              >
                <Button
                  onClick={closeProfile}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-close"} />}
                  variant={"secondary"}
                />
                <Stack
                  direction={"horizontal"}
                  items={"center"}
                  gap={"sm"}
                  className={"overflow-hidden"}
                >
                  {canManageCandidate && (
                    <ButtonIcon
                      onClick={handleEditApplicant}
                      variant={"secondary"}
                      icon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square text-lg"} />}
                    >
                      {t("common:edit")}
                    </ButtonIcon>
                  )}
                  <DropdownMenu>
                    {canManageCandidate && applicantFull && (
                      <DropdownMenuItem
                        onClick={handleMergeWith}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-code-merge"} />}
                        title={t("ats:merge_with") + "..."}
                      />
                    )}
                    {applicantFull && (
                      <DropdownMenuItem
                        onClick={handleOpenIgnored}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-user-xmark"} />}
                        title={t("ats:rejected_duplicates") + "..."}
                      />
                    )}
                    {canIEdit(ApiPermission.delete_candidates) && (
                      <DropdownMenuItem
                        onClick={handleDelete}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                        variant={"menuDanger"}
                        title={t("common:delete") + "..."}
                      />
                    )}
                  </DropdownMenu>
                </Stack>
              </Stack>
            </Box>
          </Box>

          <Stack
            gap={"md"}
            className={"-mt-28 top-16 w-full px-6 pt-(--gap-lg) pb-(--gap-xl)"}
            items={"center"}
          >
            {canManageCandidate && (
              <DropdownMenu
                trigger={
                  <Box className={"relative w-41 h-41 group cursor-pointer "}>
                    <Avatar
                      size={"2xl"}
                      url={applicant.photo_url}
                      variant={"opaque"}
                      acronym={`${applicant.first_name} ${applicant.last_name}`}
                    />
                    <Box
                      className={
                        "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                      }
                    >
                      <FontAwesomeIcon
                        icon={"fa-light fa-camera"}
                        className={"text-[40px] text-light "}
                      />
                    </Box>
                  </Box>
                }
              >
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-image"} />}
                  onClick={handleAvatarUpload}
                  title={t("common:upload_new")}
                />
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                  variant={"menuDanger"}
                  onClick={handleAvatarDelete}
                  title={t("common:delete")}
                />
              </DropdownMenu>
            )}
            {!canManageCandidate && (
              <Box className={"relative"}>
                <Avatar
                  size={"2xl"}
                  url={applicant.photo_url}
                  variant={"opaque"}
                  acronym={`${applicant.first_name} ${applicant.last_name}`}
                />
              </Box>
            )}

            {isConfigPending ? (
              <Stack items={"center"} gap={"sm"}>
                <Skeleton className={"h-7 w-48"} />
                <Skeleton className={"h-5 w-32"} />
              </Stack>
            ) : (
              <Stack items={"center"} className={"text-lg"}>
                <Title
                  className={"text-center"}
                  header={[
                    applicant.first_name,
                    hasField(ApiFieldTypes.applicant_middle_name)
                      ? applicant.middle_name
                      : undefined,
                    applicant.last_name,
                  ]
                    .filter((name) => name?.trim())
                    .join(" ")}
                  size={"lg"}
                />
                {hasField(ApiFieldTypes.applicant_position) &&
                  applicant.current_position.position && (
                    <Text className={"text-secondary"}>{applicant.current_position.position}</Text>
                  )}
                {hasField(ApiFieldTypes.applicant_company) &&
                  applicant.current_position.company && (
                    <Text className={"text-secondary"}>{applicant.current_position.company}</Text>
                  )}
              </Stack>
            )}
            {!!applicant.potential_threat?.length && (
              <PotentialThreatTag threats={applicant.potential_threat} />
            )}
            {applicantFull?.employee && <HiredTag employee={applicantFull.employee} />}
            {applicantFull && <ApplicantLabels applicant={applicantFull} />}
          </Stack>
          {hasSimilarApplicants && (
            <Stack className={"border-b border-t border-dark/5 p-6"} gap={"sm"}>
              <Avatar url={applicantFull.similar_applicants[0].photo_url} size={"md"} />
              <Stack>
                <Text className={"text-warning font-semibold"}>
                  {t("ats:similar_candidate_found_title")}
                </Text>
                <Text className={"text-secondary"}>
                  {t("ats:similar_candidate_found_description")}
                </Text>
              </Stack>
              <Box>
                <Button onClick={handleMerge}>{t("common:review")}</Button>
              </Box>
            </Stack>
          )}

          <Stack className={twMerge("px-6 pb-6", hasSimilarApplicants && "pt-6")}>
            <ApplicantProfileFields
              applicant={applicant}
              customFields={applicantFull?.custom_fields}
              applicantFull={applicantFull}
            />
            <Tabs
              className={"pt-(--gap-xl)"}
              onActiveTabChange={handleActiveTabChange}
              activeTab={activeTab}
            >
              {applicantFull &&
                applicantFull.cvs?.map((cv, index) => (
                  <TabItem title={cv.source.title} key={`resume-tab-${index}`}>
                    <ApplicantProfileCV applicant={applicantFull} cv={cv} />
                  </TabItem>
                ))}
              <TabItem title={t("core:notes")} rightIcon={getNotesTabRightIcon()}>
                {applicantFull && (
                  <ApplicantProfileComments
                    applicant={applicantFull}
                    isNotesTabActive={openNotes}
                  />
                )}
              </TabItem>
            </Tabs>
          </Stack>
        </Stack>
      )}
    </>
  );
};
